import { useEffect, useState } from 'react'
import Cookies from 'js-cookie';
import { getRdRentalInvoiceDetails, getRdRentalInvoicesByTenantId } from '../../../services/Invoice';
import Pagination from '../../../components/Pagination';
import { useTheme } from '../../../components/hooks/useTheme';
import useTenant from '../../../components/hooks/useTenant';
import jsPDF from "jspdf";
import { Dialog } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PleaseWaitPopup from '../../PleaseWaitPopup';
import moment from 'moment';
import useGeneral from '../../../components/hooks/useGeneral';

const heading = ["#", "Issued On", "Total Amount", "Status"];

const formatBillingCycle = (value?: string | null) => {
  if (!value) return "";

  const trimmed = value.trim();
  if (!trimmed) return "";

  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

type InvoiceProps = {
  isRefresh: boolean;
  setIsRefresh: (value: boolean) => void;
}

const Invoice = ({
  isRefresh,
  setIsRefresh
}: InvoiceProps) => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { themeStyles, theme } = useTheme();
  const { handleTenantError }: any = useTenant();
  const { dateFormat }: any = useGeneral();

  const textColor =
    theme === "dark"
      ? `${themeStyles.main.textSubHeadingColor}`
      : `${themeStyles.main.textHeadingColor}`;

  const [creditCardData, setCreditCardData] = useState<any[]>([]);
  const [paginationModel, setPaginationModel] = useState<{ page: number, size: number }>({
    page: 0,
    size: 5
  });
  const [totalLength, setTotalLength] = useState<number>(0);
  const [showEntries, setShowEntries] = useState<number>(5);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [openPdfDialog, setOpenPdfDialog] = useState<boolean>(false);
  const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);

  useEffect(() => {
    if (isRefresh) {
      setIsRefresh(false);
      setPaginationModel({
        ...paginationModel,
        page: 0
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefresh])

  useEffect(() => {
    if (tenantId) {
      getRdRentalInvoicesByTenantId(tenantId, paginationModel.page, paginationModel.size)
        .then(res => {
          setTotalLength(res?.count)
          const data = res?.rdRentalInvoice?.map((item: any) => ({
            ...item,
            invoiceDate: item.invoiceDate
              ? moment(new Date(item.invoiceDate)).format(dateFormat + "")
              : "",
            totalAmount: (item?.amountDue ?? 0) + (item?.amountPaid ?? 0),
          }))
          setCreditCardData(data);
          handleTenantError(null);
        })
        .catch(error => {
          handleTenantError(error);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, paginationModel])

  const handlePreviewPdf = async (item: any) => {
    try {
      setPleaseWaitPopup(true);
      const res = await getRdRentalInvoiceDetails(item?.id);
      generateInvoicePdf(res);
    } catch (error) {
      handleTenantError(error);
    }
  }

  const generateInvoicePdf = async (data: any) => {
    const doc = new jsPDF();

    let y = 15;

    doc.setFillColor(41, 128, 185);
    doc.rect(0, 0, 210, 32, "F");

    doc.addImage(
      window.location.origin + "/images/logo/datarepo-logo.png",
      "PNG",
      10,
      5,
      38,
      18
    );

    doc.setTextColor(255, 255, 255);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("DataRepo", 60, 14);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("RdRental - Subscription Invoice", 60, 22);

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("INVOICE", 190, 16, { align: "right" });

    y = 42;

    doc.setTextColor(0, 0, 0);
    doc.setFillColor(245, 247, 250);
    doc.roundedRect(10, y - 5, 190, 22, 3, 3, "F");

    doc.setFontSize(11);
    doc.text(`Invoice #: ${data?.invoiceNumber}`, 15, y + 5);

    doc.text(
      `Date: ${data?.invoiceDate
        ? moment(data?.invoiceDate).format(dateFormat)
        : ""
      }`,
      120,
      y + 5
    );

    doc.text(`Status: ${data?.status}`, 15, y + 13);

    y += 30;

    const section = (title: string) => {
      doc.setFillColor(52, 152, 219);
      doc.rect(10, y - 5, 190, 8, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.text(title, 12, y);

      doc.setTextColor(0, 0, 0);
      y += 12;
    };

    section("Customer Details");

    doc.setFont("helvetica", "normal");
    doc.text(`Name: ${data?.customerName}`, 10, y);
    doc.text(`Email: ${data?.email}`, 120, y);

    y += 12;

    section("Subscription Details");

    doc.text(`Plan: ${data?.planName}`, 10, y);
    doc.text(`Billing: ${formatBillingCycle(data?.billingCycle)}`, 120, y);

    y += 15;

    const planPrice = data?.planPrice ?? 0;
    const discount = data?.discount ?? 0;
    const totalPaid = data?.finalAmount ?? 0;
    const outstanding = data?.balance;

    const taxes = data?.taxes?.length
      ? data.taxes
      : [];

    const baseHeight = 28;
    const taxHeight = taxes.length * 6;
    const extra = 18;

    const boxHeight = baseHeight + taxHeight + extra;


    doc.setFillColor(236, 240, 241);
    doc.roundedRect(10, y - 5, 190, boxHeight, 3, 3, "F");

    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);

    doc.text("Plan Price", 15, y + 5);
    doc.text("Discount", 15, y + 12);

    doc.text(`$${planPrice.toFixed(2)}`, 190, y + 5, { align: "right" });
    doc.text(`$${discount.toFixed(2)}`, 190, y + 12, { align: "right" });

    let taxY = y + 19;

    taxes.forEach((tax: any) => {
      doc.text(`${tax.taxName} (${tax.taxRate}%)`, 15, taxY);

      doc.text(`$${Number(tax.taxAmount || 0).toFixed(2)}`, 190, taxY, {
        align: "right",
      });

      taxY += 6;
    });

    doc.setFont("helvetica", "bold");
    doc.setTextColor(39, 174, 96);

    doc.text("Total Paid", 15, taxY + 2);
    doc.text(`$${totalPaid.toFixed(2)}`, 190, taxY + 2, {
      align: "right",
    });

    doc.setTextColor(231, 76, 60);

    doc.text("Outstanding", 15, taxY + 10);
    doc.text(`$${outstanding?.toFixed(2)}`, 190, taxY + 10, {
      align: "right",
    });

    doc.setTextColor(0, 0, 0);

    y += boxHeight + 10;

    section("Payment Info");

    doc.setFont("helvetica", "normal");
    doc.text(`Method: ${data?.paymentType}`, 10, y);
    doc.text(`Transaction ID: ${data?.transactionId}`, 10, y + 8);

    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      "This invoice is generated by DataRepo system (RdRental product).",
      105,
      285,
      { align: "center" }
    );

    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);

    setPdfUrl(url);
    setOpenPdfDialog(true);
    setPleaseWaitPopup(false);
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    }
  }, [pdfUrl]);

  const handleClose = () => {
    setOpenPdfDialog(false);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  }

  return (
    <>
      <div className="w-full border border-[#167AC3] rounded-md">
        <div className="flex justify-between items-center w-full h-[50px] p-2.5 bg-[#167AC3] text-white rounded-tl-lg rounded-tr-lg">
          <h4 className="font-medium text-white">Invoices</h4>
        </div>

        <div className="flex flex-col p-4 gap-3">
          <table className='w-full'>
            <thead className="border-b-2 border-[#3490dc]">
              <tr className="text-sm text-left">
                {heading.map((item, index) => (
                  <th
                    key={index}
                    className={`border-r ${themeStyles.main.borderColor} text-left p-0.5 ${textColor}`}
                    style={{
                      textAlign: item === "Status" ? "center" : "left"
                    }}
                  >
                    {item}
                  </th>
                ))}
                <th
                  className={`text-center p-0.5 ${textColor}`}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="border-b border-[#3490dc]">
              {creditCardData?.map((item, index) => (
                <tr
                  key={index}
                  className={`text-left text-sm ${textColor}`}
                >
                  <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                    {item?.id}
                  </td>

                  <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                    {item?.invoiceDate}
                  </td>

                  <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                    ${item?.totalAmount === null ? "0.00" : (parseFloat(item?.totalAmount))?.toFixed(2)}
                  </td>

                  <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-center !py-3 text-sm p-0.5 ${textColor}`}>
                    <span className="bg-[#4ade80] py-1 px-2 border-radius-[0.375rem] text-white rounded-[4px]">
                      {item?.status}
                    </span>
                  </td>

                  <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-center text-sm p-0.5 ${textColor}`}>
                    <span className="flex justify-center items-center text-[0.875rem] text-[#3b82f6]">
                      <button
                        title="Preview Invoice PDF"
                        className="hover:opacity-80 transition"
                        onClick={() => handlePreviewPdf(item)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm0 2.5L18.5 9H14zM8 13h8v2H8zm0 4h8v2H8zM8 9h5v2H8z" />
                        </svg>
                      </button>
                    </span>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            totalLength={totalLength}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            showEntries={showEntries}
            setShowEntries={setShowEntries}
          />
        </div>
      </div>

      <Dialog
        open={openPdfDialog}
        onClose={() => setOpenPdfDialog(false)}
        fullWidth
        maxWidth="lg"
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            height: '90vh',
            borderRadius: '16px',
          },
        }}
      >
        <div className="flex justify-between items-center p-3 border-b">
          <span className="font-medium">Invoice Preview</span>

          <button
            type="button"
            onClick={handleClose}
            className="cursor-pointer"
          >
            <CloseIcon />
          </button>
        </div>

        {pdfUrl && (
          <iframe
            id="pdf-frame"
            src={pdfUrl}
            className="w-full h-full"
            title="Invoice Preview"
          />
        )}
      </Dialog>

      <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
    </>
  );
}

export default Invoice;